/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TextInput,
  ScrollView,
  useWindowDimensions,
  TouchableOpacity,
} from "react-native";
import { useTheme } from "../../../../theme";

export interface TargetRow {
  Week: string;
  DPS_Goal: number;
  DPS_Actual: string;
  CPH_Goal: number;
  CPH_Actual: string;
  Gap_Time_Goal: number;
  Gap_Time_Actual: string;
  SIA_Goal: number;
  SIA_Actual: string;
  period: string;
  week: string;
  originalMetric?: any;
  isReadOnly?: boolean; // Add this property to track row-level read-only status
}

interface TargetsDisplayTableProps {
  testID?: string;
  metricsData: TargetRow[];
  onMetricUpdate?: (updatedMetrics: TargetRow[]) => void;
  onEmployeeNameClick?: () => void;
  isViewOnlyMode?: boolean;
}

const TargetsDisplayTable: React.FC<TargetsDisplayTableProps> = ({
  testID = "targets-display-table",
  metricsData,
  onMetricUpdate,
  onEmployeeNameClick,
  isViewOnlyMode = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";

  // Memoize table data to prevent unnecessary re-renders
  const tableData = useMemo(() => metricsData, [metricsData]);

  // Deep comparison function for metrics
  const hasMetricsChanged = useCallback(
    (newMetrics: TargetRow[], prevMetrics: TargetRow[]) => {
      if (newMetrics.length !== prevMetrics.length) {
        return true;
      }
      return newMetrics.some((newMetric, index) => {
        const prevMetric = prevMetrics[index];
        return Object.keys(newMetric).some((key) => {
          if (key === "originalMetric") {
            return false;
          } // Skip comparing originalMetric
          return (
            newMetric[key as keyof TargetRow] !==
            prevMetric[key as keyof TargetRow]
          );
        });
      });
    },
    []
  );

  // Handle input change with proper memoization and validation
  const handleInputChange = useCallback(
    (rowIndex: number, field: keyof TargetRow, value: string) => {
      if (!tableData || !tableData[rowIndex]) {
        return;
      }
      
      // Check if this specific row is read-only - don't allow changes
      if (tableData[rowIndex]?.isReadOnly || isViewOnlyMode) {
        return;
      }

      let formattedValue = value;
      if (
        [
          "DPS_Actual",
          "CPH_Actual",
          "Gap_Time_Actual",
          "SIA_Actual",
          "DPS_Goal",
          "CPH_Goal",
          "Gap_Time_Goal",
          "SIA_Goal",
          "period",
        ].includes(field)
      ) {
        // Allow decimal points and numbers
        let numericValue = value.replace(/[^0-9.]/g, "");
        // Ensure only one decimal point
        numericValue = numericValue.replace(/(\..*)\./g, "$1");
        formattedValue = numericValue;
      }
      if (field === "week" || field === "Week") {
        // Only allow integer values
        formattedValue = value.replace(/[^0-9]/g, "");
        // Prevent defaulting to 0 if empty
        if (formattedValue === "") {
          // Set to empty string, do not set to 0
          formattedValue = "";
        }
      }

      const newData = [...tableData];
      // Only update the field, do not update originalMetric for string fields like 'week' and 'Week'
      const updatedRow = {
        ...newData[rowIndex],
        [field]: formattedValue,
      };
      // Only update originalMetric for metric fields
      if (
        [
          "DPS_Actual",
          "CPH_Actual",
          "Gap_Time_Actual",
          "SIA_Actual",
          "DPS_Goal",
          "CPH_Goal",
          "Gap_Time_Goal",
          "SIA_Goal",
          "period",
        ].includes(field)
      ) {
        updatedRow.originalMetric = {
          ...((newData[rowIndex] && newData[rowIndex].originalMetric) || {}),
          [field]: formattedValue !== "" ? parseFloat(formattedValue) : null,
        };
      }
      newData[rowIndex] = updatedRow;

      // Fix: Do not mutate the original tableData array, always use a new array for onMetricUpdate
      if (onMetricUpdate) {
        onMetricUpdate([...newData]);
      }
    },
    [tableData, onMetricUpdate]
  );

  // Memoize column definitions to prevent unnecessary re-renders
  const columnHeaders = useMemo(
    () => [
      { key: "Week", label: "Week" },
      { key: "DPS_Goal", label: "DPS Goal" },
      { key: "DPS_Actual", label: "DPS Actual", isInput: true },
      { key: "CPH_Goal", label: "CPH Goal" },
      { key: "CPH_Actual", label: "CPH Actual", isInput: true },
      { key: "Gap_Time_Goal", label: "Gap Time Goal" },
      { key: "Gap_Time_Actual", label: "Gap Time Actual", isInput: true },
      { key: "SIA_Goal", label: "SIA Goal" },
      { key: "SIA_Actual", label: "SIA Actual", isInput: true },
      { key: "period", label: "Period", isInput: true },
      { key: "week", label: "Week", isInput: true },
    ],
    []
  );

  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // Memoize input rendering to prevent unnecessary re-renders
  const renderInput = useCallback(
    (value: string, rowIndex: number, field: keyof TargetRow) => {
      // Check if this specific row is read-only
      const isRowReadOnly = tableData[rowIndex]?.isReadOnly || isViewOnlyMode;
      
      return (
        <View style={styles.inputContainer}>
          <TextInput
            style={[
              styles.input,
              { color: isDarkTheme ? colors.text.primary : "#2D2D2D" },
              isMobile && { fontSize: 14, padding: 6, height: 40 },
              {
                backgroundColor: isRowReadOnly
                  ? isDarkTheme
                    ? colors.background.elevated
                    : "#f5f5f5"
                  : isDarkTheme
                  ? colors.background.surface
                  : "#ffffff",
                borderColor: isDarkTheme ? colors.border.default : "#ced4da",
              },
            ]}
            value={value}
            onChangeText={(newValue) =>
              handleInputChange(rowIndex, field, newValue)
            }
            keyboardType="numeric"
            placeholder="0"
            placeholderTextColor={
              isDarkTheme ? colors.text.secondary : "#616161"
            }
            editable={!isRowReadOnly}
            testID={`${field}-input-${rowIndex}`}
            pointerEvents={isRowReadOnly ? "none" : "auto"}
            accessibilityLabel={`${field.replace(/_/g, " ")} input`}
            textAlign="center"
            onBlur={() => {
              // Format value on blur
              if (value && !isNaN(parseFloat(value))) {
                const formatted = parseFloat(value).toString();
                handleInputChange(rowIndex, field, formatted);
              }
            }}
          />
        </View>
      );
    },
    [
      colors.background.elevated,
      colors.background.surface,
      colors.border.default,
      colors.text.primary,
      colors.text.secondary,
      handleInputChange,
      isDarkTheme,
      isMobile,
      isViewOnlyMode,
      tableData,
    ]
  );

  const tableContent = (
    <View
      style={[
        styles.container,
        isDarkTheme && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
        },
      ]}
    >
      {/* Table Header */}
      <View style={styles.headerRow}>
            {columnHeaders.map((column) => (
              <View
                key={column.key}
                style={[styles.cell, isMobile && { padding: 8, minWidth: 120 }]}
              >
                <Text
                  style={[
                    styles.headerText,
                    column.key === "Week" && { backgroundColor: "#3582E6" },
                    isMobile && { fontSize: 13 },
                  ]}
                >
                  {column.label}
                </Text>
              </View>
            ))}
          </View>

          {/* Table Body */}
          <View style={styles.tableBody}>
            {tableData && tableData.length > 0 ? (
              tableData.map((row, rowIndex) => (
                <View
                  key={`row-${rowIndex}`}
                  style={[
                    styles.row,
                    rowIndex % 2 === 1 && styles.alternatingRow,
                    row.isReadOnly && styles.readOnlyRow, // Add visual indicator for read-only rows
                    isDarkTheme && {
                      borderBottomColor: colors.border.divider,
                      backgroundColor:
                        row.isReadOnly || rowIndex % 2 === 1
                          ? colors.background.elevated
                          : colors.background.surface,
                    },
                  ]}
                >
                  {columnHeaders.map((column) => (
                    <View
                      key={`cell-${rowIndex}-${column.key}`}
                      style={[
                        styles.cell,
                        isMobile && { padding: 8, minWidth: 120 },
                        isDarkTheme && { borderRightColor: colors.border.divider },
                      ]}
                    >
                      {column.key === "Week" && onEmployeeNameClick ? (
                        <TouchableOpacity onPress={onEmployeeNameClick}>
                          <Text
                            style={[
                              styles.cellText,
                              isMobile && { fontSize: 14 },
                            ]}
                          >
                            {row[column.key as keyof TargetRow] || ""}
                          </Text>
                        </TouchableOpacity>
                      ) : column.isInput ? (
                        renderInput(
                          String(row[column.key as keyof TargetRow] || ""),
                          rowIndex,
                          column.key as keyof TargetRow
                        )
                      ) : (
                        <Text
                          style={[
                            styles.cellText,
                            isMobile && { fontSize: 14 },
                            isDarkTheme && { color: colors.text.primary },
                          ]}
                        >
                          {column.key.endsWith("_Goal")
                            ? String(row[column.key as keyof TargetRow] || "0")
                            : String(
                                row[column.key as keyof TargetRow] || ""
                              )}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              ))
            ) : (
              <View style={styles.noDataRow}>
                <Text
                  style={[
                    styles.noDataText,
                    isDarkTheme && { color: colors.text.secondary },
                  ]}
                >
                  No metrics data available
                </Text>
              </View>
            )}
          </View>
        </View>
  );

  return (
    <View testID={testID} style={styles.tableWrapper}>
      {Platform.OS === "web" ? (
        tableContent
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          style={styles.horizontalScrollView}
        >
          {tableContent}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tableWrapper: {
    width: "100%",
    marginBottom: 16,
    alignSelf: "stretch",
  },
  inputContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  horizontalScrollView: {
    width: "100%",
    alignSelf: "stretch",
    flexGrow: 1,
  },
  tableTitle: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#FFFFFF",
  },
  container: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: "white",
    marginBottom: 16,
    width: Platform.OS === "web" ? "100%" : undefined,
    minWidth: Platform.OS === "web" ? "100%" : undefined,
  },
  input: {
    fontSize: Platform.OS === "web" ? 14 : 14,
    padding: Platform.OS === "web" ? 4 : 6,
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 4,
    width: "70%",
    textAlign: "center",
    height: Platform.OS === "web" ? 32 : 36,
    backgroundColor: "#ffffff",
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#3582E6",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    width: "100%",
  },
  headerCell: {
    flex: 1,
    padding: Platform.OS === "web" ? 12 : 10,
    alignItems: "center",
    minWidth: Platform.OS === "web" ? 100 : 120,
    justifyContent: "center",
    // Ensure header text is centered for each column
    textAlign: "center",
  },
  headerText: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 14 : 14,
    color: "white",
    textAlign: "center", // Ensure header text is centered
  },
  tableBody: {
    width: "100%",
    alignSelf: "stretch",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    backgroundColor: "#ffffff",
    width: "100%",
  },
  alternatingRow: {
    backgroundColor: "#f8f9fa",
  },
  cell: {
    flex: 1,
    padding: Platform.OS === "web" ? 8 : 6,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#eee",
    minWidth: Platform.OS === "web" ? 120 : 120,
    textAlign: "center",
  },
  weekCell: {
    flex: 0.8,
    padding: Platform.OS === "web" ? 12 : 10,
    backgroundColor: "#0066cc", // header only, not for body
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
    minWidth: Platform.OS === "web" ? 120 : 120,
  },
  cellText: {
    fontSize: Platform.OS === "web" ? 14 : 14,
    fontWeight: "500",
    color: "#2D2D2D",
    textAlign: "center",
  },
  noDataRow: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  noDataText: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    color: "#616161",
    textAlign: "center",
    fontStyle: "italic",
  },
  readOnlyRow: {
    backgroundColor: "#f5f5f5", // Light gray background for completed week rows
    opacity: 1,
    // borderLeftWidth: 4, // Add left border as a visual indicator
    // borderLeftColor: "#0066cc", // Blue border to indicate completed/past weeks
  },
});

export default TargetsDisplayTable;
